import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { LogIn } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";

/** Only same-origin absolute paths are honoured as a post-login destination. */
function safeRedirect(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  if (!v.startsWith("/") || v.startsWith("//") || v.includes("://")) return undefined;
  return v;
}

export const Route = createFileRoute("/_site/account/login")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const redirect = safeRedirect(search.redirect);
    return redirect ? { redirect } : {};
  },
  head: () => ({
    meta: [
      { title: "Sign in to your account — NexKey.gg" },
      { name: "description", content: "Sign in to your NexKey account to sync your wishlist across devices." },
      { property: "og:title", content: "Sign in to your account — NexKey.gg" },
      { property: "og:description", content: "Sign in to your NexKey account to sync your wishlist across devices." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AccountLogin,
});

function AccountLogin() {
  const { t } = useSettings();
  const { login } = useAuth();
  const navigate = useNavigate();
  const router = useRouter();
  const { redirect } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError(t("account.error.required"));
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      if (redirect) {
        navigate({ href: redirect, replace: true });
        return;
      }
      const canGoBack = router.history.canGoBack?.();
      if (canGoBack) router.history.back();
      else navigate({ to: "/account", replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("account.error.login"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto grid place-items-center px-4 py-16">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-glow">
        <h1 className="flex items-center gap-2 font-display text-xl font-bold">
          <LogIn className="h-5 w-5 text-primary" /> {t("account.login.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("account.login.subtitle")}</p>

        <label className="mt-6 block">
          <span className="text-xs font-medium text-muted-foreground">{t("account.email")}</span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </label>
        <label className="mt-4 block">
          <span className="text-xs font-medium text-muted-foreground">{t("account.password")}</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </label>

        {error && <p className="mt-4 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="mt-6 w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:brightness-110 disabled:opacity-60"
        >
          {loading ? t("account.loading") : t("account.login.cta")}
        </button>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          {t("account.login.noAccount")}{" "}
          <Link to="/account/register" className="font-semibold text-primary hover:underline">
            {t("account.register.cta")}
          </Link>
        </p>
      </form>
    </div>
  );
}
